import { relations } from "drizzle-orm/_relations";
import {
  boolean,
  date,
  doublePrecision,
  index,
  integer,
  pgTable,
  serial,
  text,
  time,
  timestamp,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { richText, timestamps } from "./shared";
import { tournamentDirectors } from "./tournament-directors";
import { tournamentDivisions } from "./tournament-divisions";
import { tournamentInviteSources } from "./tournament-invite-sources";
import { tournamentInvites } from "./tournament-invites";
import { venues } from "./venues";

const { createInsertSchema, createSelectSchema, createUpdateSchema } =
  createSchemaFactory({ zodInstance: z });

export const tournaments = pgTable(
  "tournaments",
  {
    id: serial().primaryKey(),
    name: text(),
    date: date().notNull(),
    startTime: time().notNull(),
    visible: boolean().notNull().default(false),
    demo: boolean().notNull().default(false),
    inviteOnly: boolean().notNull().default(false),
    registrationOpenDate: date(),
    registrationOpenAt: timestamp(),
    venueId: integer()
      .notNull()
      .references(() => venues.id),
    imageSource: text(),
    description: richText(),
    pointsMultiplier: doublePrecision().notNull().default(1.0),
    externalRef: text(),
    ...timestamps,
  },
  (table) => [
    index("tournaments_date_idx").on(table.date),
    index("tournaments_venue_id_idx").on(table.venueId),
  ],
);

export const selectTournamentSchema = createSelectSchema(tournaments);
export const createTournamentSchema = createInsertSchema(tournaments).omit({
  id: true,
  externalRef: true,
});
export const updateTournamentSchema = createUpdateSchema(tournaments);

export type Tournament = z.infer<typeof selectTournamentSchema>;
export type CreateTournament = z.infer<typeof createTournamentSchema>;
export type UpdateTournament = z.infer<typeof updateTournamentSchema>;

export const tournamentRelations = relations(tournaments, ({ one, many }) => ({
  venue: one(venues, {
    fields: [tournaments.venueId],
    references: [venues.id],
  }),
  tournamentDivisions: many(tournamentDivisions),
  directors: many(tournamentDirectors),
  invites: many(tournamentInvites),
  inviteSources: many(tournamentInviteSources),
}));
